import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, GoogleLogo, User, Buildings, X, GraduationCap, Pulse } from "@phosphor-icons/react";
import { firebaseReady, googleSignIn, humanizeFirebaseError } from "@/lib/firebase";
import { startSocialPulseLogin } from "@/lib/socialpulse";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import CountrySelect from "@/components/CountrySelect";

const POLICIES = [
  { key: "terms", label: "Terms of Use", to: "/terms" },
  { key: "privacy", label: "Privacy Policy", to: "/privacy" },
  { key: "cookie", label: "Cookie Policy", to: "/cookie" },
  { key: "memorial", label: "Memorial & Obituary Policy", to: "/memorial-policy" },
];
const YEARS = Array.from({ length: 90 }, (_, i) => String(new Date().getFullYear() - 13 - i));

export default function Register() {
  const { register, firebaseLogin } = useAuth();
  const nav = useNavigate();
  const [accountType, setAccountType] = useState("individual");
  const [f, setF] = useState({ first_name: "", last_name: "", org_name: "", org_address: "", contact_person: "", org_email: "", org_phone: "", gender: "", year_of_birth: "", country: "", state: "", city: "", email: "", password: "" });
  const [agree, setAgree] = useState(false);
  const [age16, setAge16] = useState(false);
  const [keep, setKeep] = useState(false);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const close = () => nav("/");

  const nameOk = accountType === "org" ? f.org_name.trim() : f.first_name.trim();
  const canSubmit = agree && age16 && nameOk && f.email.trim() && f.password.length >= 6;

  const buildPayload = () => ({
    ...f, account_type: accountType,
    name: accountType === "org" ? f.org_name : `${f.first_name} ${f.last_name}`.trim(),
    consents: { terms: agree, privacy: agree }, age_confirmed: age16, keep_signed_in: keep,
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!agree) { toast.error("Please agree to the Terms of Use and Privacy Policy to continue"); return; }
    if (!age16) { toast.error("You must confirm you are 16 or older"); return; }
    setLoading(true);
    try {
      await register(buildPayload());
      toast.success("Account created!");
      nav("/onboarding");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const google = async () => {
    if (!agree || !age16) { toast.error("Please agree to the Terms & Privacy and confirm you are 16 or older"); return; }
    try {
      const idToken = await googleSignIn();
      await firebaseLogin(idToken, keep);
      toast.success("Welcome to AlumniPulse!");
      nav("/onboarding");
    } catch (err) { toast.error(humanizeFirebaseError(err)); }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-black/60 p-4 backdrop-blur-sm sm:p-6"
      data-testid="register-modal"
      onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}
    >
      <div className="relative my-4 w-full max-w-md rounded-2xl border border-border/60 bg-card p-6 shadow-2xl sm:my-8 sm:p-8">
        <button type="button" onClick={close} aria-label="Close" data-testid="register-close"
          className="absolute right-3 top-3 grid h-9 w-9 place-items-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground">
          <X size={18} />
        </button>
        <div className="mb-6 flex items-center gap-2.5 pr-8">
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-primary text-primary-foreground"><GraduationCap weight="fill" size={20} /></span>
          <div>
            <h1 className="font-heading text-xl font-semibold leading-tight">Register &amp; Join AlumniPulse™</h1>
            <p className="mt-0.5 text-xs text-muted-foreground">Powered by SocialPulse® — one account across the network.</p>
          </div>
        </div>
        <form onSubmit={submit} className="space-y-5" data-testid="register-form">
        {/* Account type */}
        <div className="grid grid-cols-2 gap-2 rounded-full border border-border/60 p-1" data-testid="account-type-toggle">
          {[["individual", "Individual", User], ["org", "Institution / Org", Buildings]].map(([val, label, Icon]) => (
            <button type="button" key={val} onClick={() => setAccountType(val)} data-testid={`account-type-${val}`}
              className={`flex items-center justify-center gap-1.5 rounded-full px-3 py-2 text-sm font-medium transition-colors ${accountType === val ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
              <Icon size={16} /> {label}
            </button>
          ))}
        </div>

        {accountType === "org" ? (
          <div className="space-y-3 rounded-xl border border-border/60 bg-muted/20 p-4" data-testid="org-details">
            <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Institution / Org details — you can add more from your institution/org profile later</p>
            <div className="space-y-1.5">
              <Label htmlFor="org">Institution / Org name <span className="text-secondary">*</span></Label>
              <Input id="org" value={f.org_name} onChange={set("org_name")} data-testid="register-org-name" placeholder="e.g. Springfield High School" />
            </div>
            <div className="space-y-1.5">
              <Label>Institution / Org address</Label>
              <Input value={f.org_address} onChange={set("org_address")} data-testid="register-org-address" placeholder="Street, city — enough for people to find you" />
            </div>
            <div className="space-y-1.5">
              <Label>Contact person</Label>
              <Input value={f.contact_person} onChange={set("contact_person")} data-testid="register-contact-person" placeholder="Auto-filled from your name — edit if different" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Institution / Org email</Label><Input type="email" value={f.org_email} onChange={set("org_email")} data-testid="register-org-email" placeholder="hello@myschool.org" /></div>
              <div className="space-y-1.5"><Label>Institution / Org phone</Label><Input value={f.org_phone} onChange={set("org_phone")} data-testid="register-org-phone" placeholder="+1 555 123 4567" /></div>
            </div>
            <p className="text-xs text-muted-foreground">You will sign in with your own email/phone — the institution/org gets its own profile page where you can add team members with Manager or Editor access.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2"><Label>First name</Label><Input value={f.first_name} onChange={set("first_name")} data-testid="register-first-name" placeholder="Aisha" /></div>
            <div className="space-y-2"><Label>Last name</Label><Input value={f.last_name} onChange={set("last_name")} data-testid="register-last-name" placeholder="Menon" /></div>
          </div>
        )}

        {accountType === "individual" && (
          <p className="text-xs text-muted-foreground">We only ask for the essentials now — you can add your course, photo and more later, whenever it helps you connect.</p>
        )}

        {/* Credentials */}
        <div className="space-y-2"><Label htmlFor="email">Email</Label><Input id="email" type="email" value={f.email} onChange={set("email")} required data-testid="register-email" placeholder="you@example.com" /></div>
        <div className="space-y-2"><Label htmlFor="password">Password</Label><Input id="password" type="password" value={f.password} onChange={set("password")} required minLength={6} data-testid="register-password" placeholder="At least 6 characters" /></div>

        {/* Consent */}
        <div className="space-y-3 rounded-xl border border-border/60 bg-muted/20 p-4" data-testid="policy-consents">
          <label className="flex items-start gap-2.5 text-sm">
            <Checkbox className="mt-0.5" checked={agree} onCheckedChange={(v) => setAgree(!!v)} data-testid="consent-agree" />
            <span>By continuing, you agree to the{" "}
              <a href="/terms" target="_blank" rel="noreferrer" className="font-medium text-secondary hover:underline" data-testid="policy-link-terms">Terms of Use</a>{" "}and acknowledge the{" "}
              <a href="/privacy" target="_blank" rel="noreferrer" className="font-medium text-secondary hover:underline" data-testid="policy-link-privacy">Privacy Policy</a>.
            </span>
          </label>
          <label className="flex items-start gap-2.5 text-sm">
            <Checkbox className="mt-0.5" checked={age16} onCheckedChange={(v) => setAge16(!!v)} data-testid="consent-age16" />
            <span>I am 16 years of age or older.</span>
          </label>
        </div>

        <label className="flex items-center gap-2.5 text-sm text-muted-foreground">
          <Checkbox checked={keep} onCheckedChange={(v) => setKeep(!!v)} data-testid="keep-signed-in" /> Keep me signed in <span className="text-xs">(off by default on shared devices)</span>
        </label>

        <Button type="submit" className="w-full rounded-full gap-2" disabled={loading || !canSubmit} data-testid="register-submit">
          {loading ? "Creating…" : "Create account"} <ArrowRight size={16} />
        </Button>

        <div className="flex items-center gap-3 text-xs text-muted-foreground"><span className="h-px flex-1 bg-border" /> or <span className="h-px flex-1 bg-border" /></div>
        <Button type="button" onClick={() => startSocialPulseLogin({ keep })} className="w-full rounded-full gap-2" data-testid="register-socialpulse"><Pulse size={17} weight="bold" /> Continue with SocialPulse</Button>

        {firebaseReady && (
          <Button type="button" variant="outline" onClick={google} className="w-full rounded-full gap-2" data-testid="register-google"><GoogleLogo size={17} weight="bold" /> Continue with Google</Button>
        )}

        <p className="text-center text-sm text-muted-foreground">
          Already a member? <Link to="/login" className="font-medium text-secondary hover:underline">Sign in</Link>
        </p>
        </form>
      </div>
    </div>
  );
}
